import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Box, ButtonBase, IconButton, Typography, type Theme } from '@mui/material';
import { FEATURE_FLAG } from '../../../../constants/featureFlags';
import { mergeSx } from '../../../../utils/styles';
import PromoIcon from '../../../icons/PromoIcon';
import { useSidebarPresentation } from '../contexts/SidebarPresentationContext';
import { NEOBET_FEATURED_TAB_PATH } from '../constants';
import { BettingDrawerLoadingState, CasinoDrawerList, SportsbookDrawerList } from './BettingDrawerLists';
import CompactBettingDrawerContent from './CompactBettingDrawerContent';
import NeobetInstallAppButton from './NeobetInstallAppButton';
import SidebarLanguageSection from './SidebarLanguageSection';
import SidebarTransition from './SidebarTransition';
import useBettingDrawerData, { type BettingDrawerData, type SectionKey } from './useBettingDrawerData';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.25,
    alignItems: 'stretch',
    px: 2,
    pb: { xs: 2, md: 1.5 },
    pt: { xs: 2, md: 0 },
  },
  section: {
    width: 1,
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: 'neutral.600',
  },
  sectionHeader: (isExpanded: boolean) => (theme: Theme) => ({
    display: 'flex',
    alignItems: 'center',
    width: 1,
    minHeight: 40,
    color: theme.palette.text.primary,
    backgroundColor: isExpanded ? 'secondary.450' : 'neutral.600',
    ...(isExpanded && {
      borderBottom: '2px solid',
      borderBottomColor: 'neutral.800',
    }),
    ...(!isExpanded && {
      '&:hover': {
        backgroundColor: 'neutral.500',
      },
    }),
  }),
  sectionHeaderButton: {
    display: 'flex',
    alignItems: 'center',
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    width: 1,
    gap: 1,
    flex: 1,
    minWidth: 0,
    px: 1.5,
    py: 1.125,
    color: 'inherit',
    textAlign: 'left',
  },
  promotionsHeaderButton: (isActive: boolean) => ({
    borderRadius: 3,
    backgroundColor: isActive ? 'secondary.450' : 'neutral.600',
    ...(isActive
      ? {
          '& .MuiTypography-root': {
            color: 'text.primary',
          },
        }
      : {
          '&:hover': {
            backgroundColor: 'neutral.500',
          },
        }),
  }),
  sectionHeaderIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 20,
    height: 20,
    flexShrink: 0,
    color: 'text.primary',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
    },
  },
  sectionHeaderTitle: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    lineHeight: '20px',
    letterSpacing: 0,
  },
  sectionToggleIcon: {
    flexShrink: 0,
    mr: 0.5,
    color: 'secondary.100',
  },
  list: {
    py: 0,
  },
} as const;

type SectionProps = {
  sectionKey: SectionKey;
  title: string;
  icon: React.ReactNode;
  isLoading: boolean;
  isExpanded: boolean;
  children: React.ReactNode;
  onHeaderClick: () => void;
  onToggle: () => void;
  toggleLabel: string;
};

const Section = ({
  sectionKey,
  title,
  icon,
  isLoading,
  isExpanded,
  children,
  onHeaderClick,
  onToggle,
  toggleLabel,
}: SectionProps) => {
  return (
    <Box sx={styles.section} data-testid={`neobet-sidebar-section-${sectionKey}`}>
      <Box sx={styles.sectionHeader(isExpanded)}>
        <ButtonBase
          onClick={onHeaderClick}
          sx={styles.sectionHeaderButton}
          aria-label={title}
          aria-expanded={isExpanded}
        >
          <Box sx={styles.sectionHeaderIcon}>{icon}</Box>
          <Typography variant='h5' sx={styles.sectionHeaderTitle}>
            {title}
          </Typography>
        </ButtonBase>
        <IconButton sx={styles.sectionToggleIcon} onClick={onToggle} aria-label={`${toggleLabel} ${title}`}>
          {isExpanded ? <ExpandLessRoundedIcon fontSize='small' /> : <ExpandMoreRoundedIcon fontSize='small' />}
        </IconButton>
      </Box>
      {isExpanded ? isLoading ? <BettingDrawerLoadingState /> : children : null}
    </Box>
  );
};

const FullBettingDrawerContent = ({ drawerData }: { drawerData: BettingDrawerData }) => {
  const {
    location,
    navigate,
    t,
    hasSportsbookAccess,
    hasCasinoAccess,
    isSectionExpanded,
    toggleExpandedSection,
    isSportsLoading,
    isSportsFetching,
    isCategoriesLoading,
  } = drawerData;
  const promotionsEnabled = useFeatureIsOn(FEATURE_FLAG.PROMOTIONS_ENABLED);
  const isPromotionsSelected = location.pathname.startsWith('/promotions');

  const casinoLabel = t('casino');
  const openLabel = t('open', 'Open');
  const closeLabel = t('close', 'Close');
  const getSectionState = (sectionKey: SectionKey) => {
    const isExpanded = isSectionExpanded(sectionKey);

    return {
      isExpanded,
      toggleLabel: isExpanded ? closeLabel : openLabel,
      onToggle: () => toggleExpandedSection(sectionKey),
    };
  };
  const casinoSection = getSectionState('casino');
  const sportsbookSection = getSectionState('sportsbook');

  return (
    <Box sx={styles.root}>
      {hasCasinoAccess ? (
        <Section
          sectionKey='casino'
          title={casinoLabel}
          icon={<CasinoRoundedIcon fontSize='small' />}
          isLoading={isCategoriesLoading}
          isExpanded={casinoSection.isExpanded}
          toggleLabel={casinoSection.toggleLabel}
          onHeaderClick={() => {
            navigate('/casino');
            casinoSection.onToggle();
          }}
          onToggle={casinoSection.onToggle}
        >
          <CasinoDrawerList drawerData={drawerData} />
        </Section>
      ) : null}

      {hasSportsbookAccess ? (
        <Section
          sectionKey='sportsbook'
          title={t('sportsbook', 'Sportsbook')}
          icon={<SportsSoccerRoundedIcon fontSize='small' />}
          isLoading={isSportsLoading || isSportsFetching}
          isExpanded={sportsbookSection.isExpanded}
          toggleLabel={sportsbookSection.toggleLabel}
          onHeaderClick={() => {
            navigate(NEOBET_FEATURED_TAB_PATH);
            sportsbookSection.onToggle();
          }}
          onToggle={sportsbookSection.onToggle}
        >
          <SportsbookDrawerList drawerData={drawerData} />
        </Section>
      ) : null}

      {promotionsEnabled ? (
        <Box sx={styles.section}>
          <ButtonBase
            onClick={() => navigate('/promotions')}
            sx={mergeSx(styles.sectionHeaderButton, styles.promotionsHeaderButton(isPromotionsSelected))}
            aria-label={t('promotions', 'Promotions')}
          >
            <Box sx={styles.sectionHeaderIcon}>
              <PromoIcon fontSize='small' sx={{ color: isPromotionsSelected ? 'primary.main' : 'text.primary' }} />
            </Box>
            <Typography variant='h5' sx={styles.sectionHeaderTitle}>
              {t('promotions', 'Promotions')}
            </Typography>
          </ButtonBase>
        </Box>
      ) : null}

      <NeobetInstallAppButton />

      <SidebarLanguageSection />
    </Box>
  );
};

const NeobetBettingDrawerContent = () => {
  const { isCompact } = useSidebarPresentation();
  const drawerData = useBettingDrawerData();

  return (
    <SidebarTransition
      isCompact={isCompact}
      expandedContent={<FullBettingDrawerContent drawerData={drawerData} />}
      compactContent={<CompactBettingDrawerContent drawerData={drawerData} isActive={isCompact} />}
    />
  );
};

export default NeobetBettingDrawerContent;
